import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcessClientOrigin,
  ProcessFlowchartNodeDetailsType,
  ProcessMapperTokenType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  DataFlowEdgeForValidation,
  ProcessEdgeForValidation,
  ProcessFlowchartForValidation,
  ProcessNodeForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {getFirstFieldIdFromLookupConfig} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildStaticDataFromFlowchartConfigGraphQL';
import {makeDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import isLookupConfigCustomMapping from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/isLookupConfigCustomMapping';
import {
  FieldDefinitionGraphQL,
  FieldMapperConfigGQL,
  ProcessFlowchartConfigGQL,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeGraphQL,
  ProcessTaskDefinitionVersionGraphQL,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {convertApplicationDataToSpec} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNetworkUtils';
import {TaskDefinitionVersionSpecWithTaskSchema} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

export interface ProcessFlowchartValidationData {
  flowchart: ProcessFlowchartForValidation;
  designSpace: ProcessFlowchartDesignSpace;
}

/**
 * Builds a ProcessFlowchartForValidation and ProcessFlowchartDesignSpace from a
 * ProcessFlowchartConfigEditorQuery GQL result.
 *
 * Extracts task-type nodes with their taskDefinitionVersionSpec and builds control flow edges
 * from the template flowchart's edgeConfigs. The designSpace is constructed from the nodes
 * and edges so that `validateProcessFlowchart` can run structural validation.
 *
 * @param flowchartConfig - The accessible ProcessFlowchartConfigNode from the GQL query
 * @returns The flowchart and designSpace, or null if the version or template flowchart is missing
 */
export default function buildProcessFlowchartForValidationFromGQL(
  flowchartConfig: ProcessFlowchartConfigGQL
): ProcessFlowchartValidationData | null {
  const version = flowchartConfig.flowchartConfigVersion;
  if (version == null) {
    return null;
  }

  const templateFlowchart = version.templateFlowchart;
  if (templateFlowchart == null) {
    return null;
  }

  const nodes: Array<ProcessNodeForValidation> = [];
  const fieldIdentifierToFieldClientId: Record<string, string> = {};

  for (const nodeConfig of templateFlowchart.nodeConfigs) {
    const nodeDetails = nodeConfig.nodeDetails;

    switch (nodeDetails.__typename) {
      case 'ProcessTaskNodeDetailsNode':
      case 'ProcessRootNodeDetailsNode':
      case 'ProcessOutputNodeDetailsNode': {
        const ptdv = nodeDetails.processTaskDefinitionVersion;
        if (isInaccessible(ptdv)) {
          continue;
        }

        const processTaskSchema = ptdv.processTaskSchema;
        if (isInaccessible(processTaskSchema)) {
          continue;
        }

        // Convert the GQL application data using the registered adapter (e.g., ProcedureFlowchartAdapter).
        // The cast to ProcessTaskDefinitionVersionGraphQL is safe because both the task node details PTDV and
        // the extracted type alias resolve to the same underlying ProcessTaskDefinitionVersionNode from the query.
        const applicationData = convertApplicationDataToSpec(
          ptdv as unknown as ProcessTaskDefinitionVersionGraphQL,
          processTaskSchema.executionType
        );

        // Build a TaskDefinitionVersionSpecWithTaskSchema with minimal schema data.
        // Procedure validation (validateProcessFlowchartForProcedure) only uses the applicationData
        // from the spec to extract MethodNodes — it does not use schema fields or field definitions.
        const taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema = {
          clientId: ptdv.id,
          currentVersionId: ptdv.id,
          inputFieldDefinitions: ptdv.inputFieldDefinitions as unknown as Array<FieldDefinitionGraphQL>,
          outputFieldDefinitions:
            ptdv.outputFieldDefinitions as unknown as Array<FieldDefinitionGraphQL> | null,
          newProcessTaskDefinitionVersion: {
            taskSchemaId: processTaskSchema.id,
            applicationData,
            taskSchema: {
              id: processTaskSchema.id,
              name: processTaskSchema.name,
              executionType: processTaskSchema.executionType,
              fields: [],
              outputSchema: null,
            },
          },
        };

        nodes.push({
          id: nodeConfig.id,
          name: nodeConfig.name,
          nodeType: nodeDetails.type,
          taskSchemaId: processTaskSchema.id,
          taskDefinitionVersionSpec,
          executionType: processTaskSchema.executionType,
        } as ProcessNodeForValidation);

        // Populate the fieldIdentifier → fieldClientIdentifier map for data-flow edge resolution.
        for (const fieldDef of ptdv.inputFieldDefinitions) {
          fieldIdentifierToFieldClientId[fieldDef.fieldIdentifier] = fieldDef.fieldClientIdentifier;
        }
        for (const fieldDef of ptdv.outputFieldDefinitions ?? []) {
          fieldIdentifierToFieldClientId[fieldDef.fieldIdentifier] = fieldDef.fieldClientIdentifier;
        }
        break;
      }
      // Router and end nodes are not relevant for procedure validation
      case 'ProcessRouterNodeDetailsNode':
      case 'ProcessEndNodeDetailsNode':
        break;

      default:
        assert(false, 'Unhandled node type when converting flowchart', {nodeDetails});
    }
  }

  const controlFlowEdges: Array<ProcessEdgeForValidation> = templateFlowchart.edgeConfigs.map(
    (edgeConfig) => ({
      id: edgeConfig.id,
      source: edgeConfig.fromNodeConfig.id,
      target: edgeConfig.toNodeConfig.id,
      type: ProcessFlowchartEdgeType.CONTROL_FLOW as const,
    })
  );

  const dataFlowEdges = buildDataFlowEdgesFromGQL(
    templateFlowchart.nodeConfigs,
    fieldIdentifierToFieldClientId
  );

  const allEdges: Array<ProcessEdgeForValidation> = [...controlFlowEdges, ...dataFlowEdges];
  const designSpace = buildDesignSpace(nodes, controlFlowEdges);

  return {
    flowchart: {
      id: templateFlowchart.id,
      nodes,
      edges: allEdges,
    },
    designSpace,
  };
}

/**
 * Constructs a ProcessFlowchartDesignSpace from the validation nodes and edges.
 * Follows the same pattern as buildValidationFlowchartFromStaticData's buildDesignSpace,
 * but works directly from the GQL-derived nodes rather than static editor data.
 */
function buildDesignSpace(
  nodes: Array<ProcessNodeForValidation>,
  edges: Array<ProcessEdgeForValidation>
): ProcessFlowchartDesignSpace {
  const taskSchemaIds = new Set<string>();
  let rootSchemaId: string | null = null;
  let rootSchemaName: string | null = null;

  for (const node of nodes) {
    if (node.hasOwnProperty('taskSchemaId')) {
      const {taskSchemaId} = node as {taskSchemaId: string};
      taskSchemaIds.add(taskSchemaId);
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) {
        rootSchemaId = taskSchemaId;
        rootSchemaName = node.name;
      }
    }
  }

  const allTaskSchemaIds = Array.from(taskSchemaIds);

  return {
    availableNodes: allTaskSchemaIds.map((schemaId) => ({
      clientId: schemaId,
      schemaId,
      name: schemaId,
      inboundMapperConfigs: [],
    })),

    availableEdges: edges.map((edge) => ({
      clientId: edge.id,
      fromNodeId: edge.source,
      toNodeId: edge.target,
    })),

    flowchartSchema: {
      __typename: 'ProcessTaskSchemaNode',
      id: rootSchemaId ?? '',
      name: rootSchemaName ?? '',
      clientOrigin: ProcessClientOrigin.PROCESSES,
      outputSchema: null,
    },

    flowchartConfig: {
      availableTaskSchemas: allTaskSchemaIds.map((schemaId) => ({
        id: schemaId,
        name: schemaId,
      })),
      schemaMapperConfigs: [],
    },
  };
}

/**
 * Builds data-flow edges from the GQL nodeConfigs' inboundSchemaMappers.
 *
 * Iterates through each node's inbound schema mappers → schema mapper dependencies → field mapper configs
 * to construct DataFlowEdgeForValidation objects. Mirrors the logic in
 * BuildStaticDataFromFlowchartConfigGraphQLRunner.buildDataFlowEdges but operates directly on
 * GQL data without requiring the full editor context.
 */
function buildDataFlowEdgesFromGQL(
  nodeConfigs: ReadonlyArray<ProcessFlowchartNodeGraphQL>,
  fieldIdentifierToFieldClientId: Record<string, string>
): Array<DataFlowEdgeForValidation> {
  const dataFlowEdges: Array<DataFlowEdgeForValidation> = [];

  for (const nodeConfig of nodeConfigs) {
    for (const inboundSchemaMapper of nodeConfig.inboundSchemaMappers) {
      const mapsToType = inboundSchemaMapper.schemaMapperConfig?.mapsToType;
      if (mapsToType == null) {
        continue;
      }

      const toFieldSectionType =
        mapsToType === ProcessMapperTokenType.PROCESS_OUTPUT
          ? TaskSchemaNodeSectionType.OUTPUTS
          : TaskSchemaNodeSectionType.INPUTS;

      for (const schemaMapperDependency of inboundSchemaMapper.schemaMapperDependencies) {
        const fromNodeId = schemaMapperDependency.fromNodeConfig.id;
        const toNodeId = inboundSchemaMapper.toNodeConfig.id;

        for (const fieldMapperConfig of schemaMapperDependency.fieldMapperConfigs) {
          const edge = buildSingleDataFlowEdge(
            fieldMapperConfig,
            fromNodeId,
            toNodeId,
            toFieldSectionType,
            fieldIdentifierToFieldClientId
          );
          if (edge != null) {
            dataFlowEdges.push(edge);
          }
        }
      }
    }
  }

  return dataFlowEdges;
}

/**
 * Builds a single DataFlowEdgeForValidation from a field mapper config.
 *
 * Returns null if the source field info cannot be determined or if field identifiers
 * cannot be resolved to client identifiers.
 */
function buildSingleDataFlowEdge(
  fieldMapperConfig: FieldMapperConfigGQL,
  fromNodeId: string,
  toNodeId: string,
  toFieldSectionType: TaskSchemaNodeSectionType,
  fieldIdentifierToFieldClientId: Record<string, string>
): DataFlowEdgeForValidation | null {
  const fromFieldInfo = getSourceFieldInfoFromFieldMapperConfig(fieldMapperConfig);
  if (fromFieldInfo == null) {
    return null;
  }

  const {fromFieldSectionType, fromFieldId} = fromFieldInfo;
  const toFieldId = fieldMapperConfig.toProcessFieldDefinition.fieldIdentifier;

  const fromFieldClientId = fieldIdentifierToFieldClientId[fromFieldId];
  const toFieldClientId = fieldIdentifierToFieldClientId[toFieldId];
  if (fromFieldClientId == null || toFieldClientId == null) {
    return null;
  }

  const sourceHandle = makeDataFlowEdgeHandle({
    nodeId: fromNodeId,
    fieldClientId: fromFieldClientId,
    sectionType: fromFieldSectionType,
    handleType: 'source',
  });
  const targetHandle = makeDataFlowEdgeHandle({
    nodeId: toNodeId,
    fieldClientId: toFieldClientId,
    sectionType: toFieldSectionType,
    handleType: 'target',
  });

  const isLookupConfigured = isLookupConfigCustomMapping(fieldMapperConfig.lookupConfig, fromFieldId);

  const transitionSource =
    fieldMapperConfig.mapsFromType === ProcessMapperTokenType.PROCESS_OUTPUT
      ? TransitionSource.OUTPUTS
      : TransitionSource.INPUTS;

  return {
    id: uuidv4(),
    source: fromNodeId,
    target: toNodeId,
    sourceHandle,
    targetHandle,
    type: ProcessFlowchartEdgeType.DATA_FLOW,
    isLookupConfigured,
    transitionSource,
  };
}

/**
 * Determines the source field section type and field identifier from a field mapper config.
 *
 * Replicates the logic from BuildStaticDataFromFlowchartConfigGraphQLRunner.getUpstreamMappingInfoFromFieldMapperConfig
 * but returns null instead of asserting on failure, since the validation path should degrade gracefully.
 */
function getSourceFieldInfoFromFieldMapperConfig(
  fieldMapperConfig: FieldMapperConfigGQL
): {fromFieldSectionType: TaskSchemaNodeSectionType; fromFieldId: string} | null {
  const fromTaskDefinitionVersion = fieldMapperConfig.fromProcessTaskDefinitionVersion;
  if (fromTaskDefinitionVersion == null || isInaccessible(fromTaskDefinitionVersion)) {
    return null;
  }

  const inputFieldDefinitions = fromTaskDefinitionVersion.inputFieldDefinitions ?? [];
  const outputFieldDefinitions = fromTaskDefinitionVersion.outputFieldDefinitions ?? [];

  const fromFieldId = getFirstFieldIdFromLookupConfig(fieldMapperConfig.lookupConfig);
  if (fromFieldId == null) {
    return null;
  }

  const isFromFieldInFieldDefinitions = (
    fieldDefinitions: ReadonlyArray<{fieldIdentifier: string}>
  ): boolean => fieldDefinitions.some((fieldDefinition) => fieldDefinition.fieldIdentifier === fromFieldId);

  // Check if the mapping comes from the flowchart start (MAP_FROM_START).
  const lookupSteps = fieldMapperConfig.lookupConfig.lookupSteps as Array<{type: string}>;
  if (lookupSteps.some((lookupStep) => lookupStep.type === LookupStepType.FLOWCHART_PROCESS_TASK)) {
    return {
      fromFieldSectionType: TaskSchemaNodeSectionType.MAP_FROM_START,
      fromFieldId,
    };
  }

  if (fieldMapperConfig.mapsFromType === ProcessMapperTokenType.PROCESS_OUTPUT) {
    if (lookupSteps[0]?.type === LookupStepType.PROCESS_TASK) {
      if (isFromFieldInFieldDefinitions(inputFieldDefinitions)) {
        return {fromFieldSectionType: TaskSchemaNodeSectionType.INPUTS, fromFieldId};
      }
    }
    if (isFromFieldInFieldDefinitions(outputFieldDefinitions)) {
      return {fromFieldSectionType: TaskSchemaNodeSectionType.OUTPUTS, fromFieldId};
    }
  } else if (isFromFieldInFieldDefinitions(inputFieldDefinitions)) {
    return {fromFieldSectionType: TaskSchemaNodeSectionType.INPUTS, fromFieldId};
  }

  // Could not determine the section — skip this edge gracefully.
  return null;
}
